import { useContext } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import classnames from 'clsx';

import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import commonFormCss from './form.module.scss';
import css from './top-level-error-container.module.scss';

function reduceFormErrors(errors = {}, touched, dedupe, baseOnly) {
  if (baseOnly && errors?.base) {
    return {
      base: errors.base,
    };
  }
  return Object.keys(errors).reduce((acc, cur) => {
    if (touched?.[cur] || cur === 'base') {
      // Deduplicate error message (i.e. error shown twice for password and confirm fields)
      const dedupeField = acc[dedupe?.[cur]]?.[0];
      if (dedupe && dedupeField === errors[cur]?.[0]) {
        return acc;
      }
      acc[cur] = errors[cur];
    }
    return acc;
  }, {});
}

const TopLevelErrorContainer = ({
  className,
  errorMessages,
  fieldOrder,
  touchedFields,
  dedupeFields,
  isPartner,
}) => {
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const values = errorMessages._values || [];
  const reducedErrors = reduceFormErrors(
    errorMessages,
    touchedFields,
    dedupeFields,
    isPartner,
  );
  return (
    <div data-testid="errorContainer">
      {!isEmpty(errorMessages) && (
        <div className={className}>
          {reducedErrors.base && (
            <div className="row">
              <div className="col-xs-12">
                <p
                  data-testid="topLevelBaseError"
                  className={classnames(commonFormCss.errorText, {
                    [css.isPartnerFlow]: isPartner,
                  })}
                >
                  {getLocalizedText(reducedErrors.base, ...values)}
                </p>
              </div>
            </div>
          )}
          {!isEmpty(omit(reducedErrors, 'base')) && (
            <div className={reducedErrors.base && css.innerSection}>
              <div className="row">
                <div className="col-xs-12">
                  <ul data-testid="topLevelErrorList" className={css.errorList}>
                    {fieldOrder.map((field) => {
                      const fieldErrors = reducedErrors[field];
                      if (Array.isArray(fieldErrors)) {
                        return fieldErrors.map((err) => {
                          const idKey = `${field}-${fieldErrors.indexOf(err)}`;
                          return (
                            <li
                              className={commonFormCss.errorText}
                              data-testid={idKey}
                              key={idKey}
                            >
                              {Array.isArray(err)
                                ? getLocalizedText(...err)
                                : getLocalizedText(err)}
                            </li>
                          );
                        });
                      }
                      return null;
                    })}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

TopLevelErrorContainer.propTypes = {
  className: PropTypes.string,
  errorMessages: PropTypes.object,
  fieldOrder: PropTypes.array.isRequired,
  touchedFields: PropTypes.object.isRequired,
  isPartner: PropTypes.bool,
};

export default TopLevelErrorContainer;
